import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { useThemeSettings, useUpdateThemeSettings, useUploadThemeAsset } from "../../api/queries";
import { ApiError } from "../../api/client";
import type { ColorPreset, ThemeColors, ThemeFont, ThemeSettings, UiLanguage } from "../../api/types";
import { Expander, Notice } from "../../design/components";
import { checkThemeContrast } from "../../design/contrast";
import { PRESET_IDS, resolvePreset } from "../../design/palettes";

const COLOR_GROUPS: { id: string; keys: (keyof ThemeColors)[] }[] = [
  { id: "backgrounds", keys: ["bg", "bgAlt", "surface", "line"] },
  { id: "text", keys: ["textHi", "textMid", "textLo"] },
  { id: "accent", keys: ["accent", "accentHi"] },
];


const FONT_STACK: Record<ThemeFont, string> = {
  inter: "Inter, sans-serif",
  barlow_semi_condensed: '"Barlow Semi Condensed", sans-serif',
  poppins: '"Poppins", sans-serif',
};

/** A segmented single-choice control: one visible row, the selected option filled. */
function Choice<T extends string>({
  label, value, options, onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <div className="theme-choice" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={value === o.value ? "on" : ""}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Row({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="theme-row">
      <div className="theme-row-text">
        <div className="theme-row-title">{title}</div>
        {hint ? <div className="theme-row-hint">{hint}</div> : null}
      </div>
      <div className="theme-row-control">{children}</div>
    </div>
  );
}

const FONT_CHOICES: ThemeFont[] = ["poppins", "inter", "barlow_semi_condensed"];

const HEX = /^#[0-9a-fA-F]{6}$/;

function ColorField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const valid = HEX.test(text);

  return (
    <label className="theme-color-field">
      <span className="theme-color-label">{label}</span>
      <span className="theme-color-input">
        <input
          type="color"
          value={valid ? text : value}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            onChange(e.target.value);
          }}
        />
        <input
          type="text"
          value={text}
          disabled={disabled}
          spellCheck={false}
          dir="ltr"
          onChange={(e) => {
            setText(e.target.value);
            if (HEX.test(e.target.value)) onChange(e.target.value);
          }}
          aria-invalid={!valid}
        />
      </span>
    </label>
  );
}

export function ThemeSettingsPage() {
  const { t } = useTranslation();
  const theme = useThemeSettings();
  const update = useUpdateThemeSettings();
  const uploadAsset = useUploadThemeAsset();

  const [colors, setColors] = useState<ThemeColors | null>(null);
  const [colorPreset, setColorPreset] = useState<ColorPreset>("hub");
  const [mode, setMode] = useState<ThemeSettings["mode"]>("light");
  const [orientation, setOrientation] = useState<ThemeSettings["sidebarOrientation"]>("vertical");
  const [font, setFont] = useState<ThemeFont>("poppins");
  const [language, setLanguage] = useState<UiLanguage>("en");
  const [appName, setAppName] = useState("");
  const [tagline, setTagline] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [seeded, setSeeded] = useState(false);

  function load(data: ThemeSettings): void {
    setColors(data.colors);
    setColorPreset(data.colorPreset);
    setMode(data.mode);
    setOrientation(data.sidebarOrientation);
    setFont(data.fontFamily);
    setLanguage(data.defaultLanguage);
    setAppName(data.appName);
    setTagline(data.tagline);
  }

  // The form is always live. It is seeded once from the server; later refetches never
  // overwrite what the admin is typing (uploads change only the logo/favicon URLs).
  useEffect(() => {
    if (seeded || !theme.data) return;
    load(theme.data);
    setSeeded(true);
  }, [theme.data, seeded]);

  const dirty =
    !!theme.data && !!colors &&
    (mode !== theme.data.mode ||
      orientation !== theme.data.sidebarOrientation ||
      font !== theme.data.fontFamily ||
      language !== theme.data.defaultLanguage ||
      colorPreset !== theme.data.colorPreset ||
      appName !== theme.data.appName ||
      tagline !== theme.data.tagline ||
      (Object.keys(colors) as (keyof ThemeColors)[]).some((k) => colors[k].toLowerCase() !== theme.data!.colors[k].toLowerCase()));

  function patchColor(key: keyof ThemeColors, value: string): void {
    setColorPreset("custom");
    setColors((current) => (current ? { ...current, [key]: value } : current));
  }

  // A preset follows the mode; a customised palette is left alone.
  function changeMode(next: ThemeSettings["mode"]): void {
    if (colorPreset !== "custom") setColors(resolvePreset(colorPreset, next));
    setMode(next);
  }

  function pickPreset(id: ColorPreset): void {
    setColorPreset(id);
    setColors(resolvePreset(id, mode));
  }

  async function save(): Promise<void> {
    if (!colors) return;
    setSaving(true);
    setError("");
    try {
      const row = await update.mutateAsync({
        colors, colorPreset, mode, sidebarOrientation: orientation, fontFamily: font, defaultLanguage: language,
        appName, tagline,
      });
      load(row);
    } catch (exc) {
      setError(exc instanceof ApiError ? exc.message : t("themeSettings.saveError", "The theme could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  function discard(): void {
    setError("");
    if (theme.data) load(theme.data);
  }

  async function pickAsset(kind: "logo" | "favicon", file: File | undefined): Promise<void> {
    if (!file) return;
    setUploadError("");
    try {
      await uploadAsset.mutateAsync({ kind, file });
    } catch (exc) {
      setUploadError(exc instanceof ApiError ? exc.message : t("themeSettings.uploadError", "The file could not be uploaded."));
    }
  }

  if (theme.isLoading || !colors) {
    return (
      <main className="page">
        <div className="page-loading" role="status" aria-label="Loading">
          <span className="page-loading-dot" />
        </div>
      </main>
    );
  }

  const nameMissing = appName.trim() === "";
  const contrast = checkThemeContrast(colors);
  const failing = contrast.filter((c) => !c.passes);

  function assetCard(kind: "logo" | "favicon", url: string | null | undefined) {
    const isLogo = kind === "logo";
    return (
      <div className="theme-asset">
        <div className="theme-asset-preview">
          {url ? (
            <img
              src={url}
              alt={isLogo ? t("themeSettings.currentLogo", "Current logo") : t("themeSettings.currentFavicon", "Current favicon")}
            />
          ) : (
            <span>{isLogo ? t("themeSettings.noLogo", "No logo set") : t("themeSettings.noFavicon", "No favicon set")}</span>
          )}
        </div>
        <div className="theme-asset-body">
          <div className="theme-row-title">{isLogo ? t("themeSettings.logo", "Logo") : t("themeSettings.favicon", "Favicon")}</div>
          <div className="theme-row-hint">
            {isLogo
              ? t("themeSettings.logoHint", "Shown in the sidebar and on the sign-in page. PNG, JPG or SVG, up to 2 MB.")
              : t("themeSettings.faviconHint", "Shown on the browser tab, including the sign-in page. PNG, ICO or SVG, up to 2 MB.")}
          </div>
          <label className="btn btn-sm">
            {isLogo ? t("themeSettings.uploadLogo", "Upload logo") : t("themeSettings.uploadFavicon", "Upload favicon")}
            <input
              type="file"
              accept={isLogo ? "image/png,image/jpeg,image/svg+xml,image/x-icon" : "image/png,image/x-icon,image/svg+xml"}
              hidden
              onChange={(e) => {
                void pickAsset(kind, e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>
    );
  }

  return (
    <main className="page theme-page">
      <div className="page-head">
        <div className="grow">
          <div className="page-title">
            <h1 className="display">{t("themeSettings.title", "Theme settings")}</h1>
          </div>
          <p className="page-sub">
            {t(
              "themeSettings.subtitle",
              "The look of the console for everyone who signs in. Changes here apply hub-wide as soon as they are saved.",
            )}
          </p>
        </div>
        <div className="page-actions theme-actions">
          {error || dirty ? (
            <span className="theme-savebar-msg" role="status">
              {error
                ? error
                : t("themeSettings.unsaved", "You have unsaved changes")}
            </span>
          ) : null}
          <button type="button" className="btn btn-sm" disabled={!dirty || saving} onClick={discard}>
            {t("themeSettings.discard", "Discard changes")}
          </button>
          <button type="button" className="btn btn-sm btn-primary" disabled={saving || nameMissing} onClick={() => void save()}>
            {t("themeSettings.save", "Save theme")}
          </button>
        </div>
      </div>

      <div className="theme-layout">
        <div className="theme-main">
          <section className="card">
            <div className="card-head">
              <h2 className="grow">{t("themeSettings.identityHeading", "Name and tagline")}</h2>
            </div>
            <div className="card-body theme-identity">
              <label className="theme-color-field">
                <span className="theme-color-label">{t("themeSettings.appName", "Application name")}</span>
                <input
                  type="text" className="theme-text" maxLength={60} value={appName}
                  aria-invalid={nameMissing}
                  onChange={(e) => { setAppName(e.target.value); }}
                />
              </label>
              <label className="theme-color-field">
                <span className="theme-color-label">{t("themeSettings.tagline", "Tagline")}</span>
                <input
                  type="text" className="theme-text" maxLength={80} value={tagline}
                  onChange={(e) => { setTagline(e.target.value); }}
                />
              </label>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h2 className="grow">{t("themeSettings.assetsHeading", "Logo and favicon")}</h2>
            </div>
            <div className="card-body theme-assets">
              {assetCard("logo", theme.data?.logoUrl)}
              {assetCard("favicon", theme.data?.faviconUrl)}
            </div>
            {uploadError ? <Notice kind="fail">{uploadError}</Notice> : null}
          </section>

          <section className="card">
            <div className="card-head">
              <h2 className="grow">{t("themeSettings.layoutHeading", "Layout and language")}</h2>
            </div>
            <div className="card-body theme-rows">
              <Row title={t("themeSettings.modeTitle", "Appearance")} hint={t("themeSettings.modeHint", "Light or dark base for the whole console.")}>
                <Choice
                  label={t("themeSettings.modeTitle", "Appearance")}
                  value={mode}
                  options={[
                    { value: "light", label: t("themeSettings.light", "Light") },
                    { value: "dark", label: t("themeSettings.dark", "Dark") },
                  ]}
                  onChange={changeMode}
                />
              </Row>
              <Row title={t("themeSettings.sidebarTitle", "Sidebar")} hint={t("themeSettings.sidebarHint", "Down the side, or as a bar across the top.")}>
                <Choice
                  label={t("themeSettings.sidebarTitle", "Sidebar")}
                  value={orientation}
                  options={[
                    { value: "vertical", label: t("themeSettings.vertical", "Vertical") },
                    { value: "horizontal", label: t("themeSettings.horizontal", "Horizontal") },
                  ]}
                  onChange={(v) => { setOrientation(v); }}
                />
              </Row>
              <Row title={t("themeSettings.languageTitle", "Default language")} hint={t("themeSettings.languageHint", "Arabic switches the console to right-to-left.")}>
                <Choice
                  label={t("themeSettings.languageTitle", "Default language")}
                  value={language}
                  options={[
                    { value: "en", label: "English" },
                    { value: "ar", label: "العربية" },
                  ]}
                  onChange={(v) => { setLanguage(v); }}
                />
              </Row>
              <Row title={t("themeSettings.fontAriaLabel", "Font")} hint={t("themeSettings.fontHint", "One typeface for the whole console.")}>
                <div className="theme-fonts" role="radiogroup" aria-label={t("themeSettings.fontAriaLabel", "Font")}>
                  {FONT_CHOICES.map((choice) => (
                    <button
                      key={choice}
                      type="button"
                      role="radio"
                      aria-checked={font === choice}
                      className={font === choice ? "on" : ""}
                      onClick={() => { setFont(choice); }}
                    >
                      <span className="theme-font-sample" style={{ fontFamily: FONT_STACK[choice] }}>Aa</span>
                      <span>{t(`themeSettings.fonts.${choice}`, choice)}</span>
                    </button>
                  ))}
                </div>
              </Row>
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <h2 className="grow">{t("themeSettings.presetsHeading", "Color preset")}</h2>
            </div>
            <div className="card-body">
              <div className="theme-presets" role="radiogroup" aria-label={t("themeSettings.presetsHeading", "Color preset")}>
                {[...PRESET_IDS, ...(colorPreset === "custom" ? (["custom"] as const) : [])].map((id) => {
                  const p = id === "custom" ? colors : resolvePreset(id, mode);
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={colorPreset === id}
                      className={colorPreset === id ? "on" : ""}
                      onClick={() => (id === "custom" ? undefined : pickPreset(id))}
                    >
                      <span className="theme-swatches" aria-hidden="true">
                        <i style={{ background: p.bg }} />
                        <i style={{ background: p.surface }} />
                        <i style={{ background: p.textHi }} />
                        <i style={{ background: p.accent }} />
                      </span>
                      <span className="theme-preset-name">{t(`themeSettings.presets.${id}`, id)}</span>
                    </button>
                  );
                })}
              </div>
              <Expander
                head={t("themeSettings.customizeColors", "Customize colors")}
                body={
                  <div className="theme-color-groups">
                    {COLOR_GROUPS.map((group) => (
                      <div key={group.id}>
                        <div className="theme-group-title">{t(`themeSettings.colorGroups.${group.id}`, group.id)}</div>
                        <div className="theme-colors">
                          {group.keys.map((key) => (
                            <ColorField
                              key={key}
                              label={t(`themeSettings.colorFields.${key}`, key)}
                              value={colors[key]}
                              disabled={false}
                              onChange={(next) => patchColor(key, next)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                    <div>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => pickPreset(colorPreset === "custom" ? "hub" : colorPreset)}
                      >
                        {t("themeSettings.resetColors", "Reset colors")}
                      </button>
                    </div>
                  </div>
                }
              />
            </div>
            {failing.length > 0 ? (
              <Notice kind="warn">
                <p>{t("themeSettings.contrastWarning", "These color pairs are below the readable-contrast guideline (4.5:1); the theme can still be saved.")}</p>
                <ul>
                  {failing.map((check) => (
                    <li key={check.id}>
                      {t(`themeSettings.contrastLabels.${check.id}`, check.id)}: {check.ratio.toFixed(2)}:1
                    </li>
                  ))}
                </ul>
              </Notice>
            ) : null}
          </section>
        </div>

      </div>

    </main>
  );
}
